<template>
    <div
        class="explore-nbr mg-container"
        :class="[theme]"
        v-if="slides.length && keywords && keywords.length === 1"
    >
        <div>
            <h2 class="mg-title">
                {{
                    $t(
                        level === 3
                            ? 'md-search-result.exploreNeighborhoodsNear'
                            : level > 0
                                ? 'md-search-result.exploreNeighborhoodsIn'
                                : 'md-search-result.exploreNeighborhoods',
                        {
                            name: lastLevel.name,
                            interpolation: { escapeValue: false }
                        }
                    )
                }}
            </h2>
            <div
                class="carousel-container"
                v-loading="taskLoading.length"
                :loading-mask="true"
            >
                <div
                    class="scroll-wrap"
                    ref="scrollWrap"
                    v-lazy-container="{
                        trigger: ['scroll']
                    }"
                    :style="{ overflow: isMobileSize ? 'auto' : 'hidden' }"
                >
                    <ul
                        ref="transitionWrap"
                        v-lazy-container="{
                            trigger: ['transitionend']
                        }"
                        :style="{
                            transform: `translateX(-${
                                startIndex * itemWidth
                            }px)`,
                            transition: '.3s all ease'
                        }"
                    >
                        <li
                            v-for="(item, index) in slides"
                            v-lazy="{
                                context: item,
                                callback: page.FEATURE_AREA_VERSION
                                    ? funcForNewFeatureArea
                                    : func
                            }"
                            ref="item"
                            :key="item.id"
                            :style="{
                                color: /template(1|2)/.test(theme)
                                    ? slideColors[index % 4]
                                    : 'normal'
                            }"
                            @click="toNeighborhoodDetail(item)"
                        >
                            <p :title="item.name">
                                <site-img
                                    class="img-home"
                                    :src="imgCdn[index % 4]"
                                    alt
                                />
                                <span>{{ item.name }}</span>
                            </p>
                            <div class="item">
                                <i class="iconfont icon-house-fill"></i>
                                <span>{{
                                    $t('md-search-result.activeListings', {
                                        count: item.counts
                                    })
                                }}</span>
                            </div>
                            <div
                                class="item"
                                v-show="item.min != null && item.max != null"
                            >
                                <i class="iconfont icon-dollor"></i>
                                <span>{{ item.priceText }}</span>
                            </div>
                        </li>
                    </ul>
                </div>
                <template v-if="/template4/.test(theme)">
                    <template v-if="theme === 'template4-listing-map'">
                        <!-- dark   -->
                        <div
                            @click="prev"
                            class="iconimg deep prev"
                            :class="{ disabled: prevDisabled }"
                        ></div>
                        <div
                            @click="next"
                            class="iconimg deep next"
                            :class="{ disabled: nextDisabled }"
                        ></div>
                    </template>
                    <template v-else>
                        <!--  light color  -->
                        <div
                            @click="prev"
                            class="iconimg light prev"
                            :class="{ disabled: prevDisabled }"
                        ></div>
                        <div
                            @click="next"
                            class="iconimg light next"
                            :class="{ disabled: nextDisabled }"
                        ></div>
                    </template>
                </template>
                <template v-else>
                    <i
                        class="iconfont iconarrow prev"
                        :class="{
                            disabled: prevDisabled,
                            'icon-v1': /template(1|2)/.test(theme),
                            'icon-v3': /template3/.test(theme),
                            'icon-arrow-up left': /broker/.test(theme)
                        }"
                        @click="prev"
                    ></i>
                    <i
                        class="iconfont iconarrow next"
                        :class="{
                            disabled: nextDisabled,
                            'icon-v1': /template(1|2)/.test(theme),
                            'icon-v3': /template3/.test(theme),
                            'icon-arrow-up right': /broker/.test(theme)
                        }"
                        @click="next"
                    ></i>
                </template>
            </div>
        </div>
    </div>
</template>

<script>
import { mapState as rootMapState } from 'pinia';
import { mapState } from '@/store/helpers';
import { usePageStore } from '@/store';

import { normalizeParams } from './_center-adaptor/normalizeParams';
import util from '@/common/util';
import {
    searchRealTimeListings,
    getNbrLinkForFeatureArea,
    getFeatureAreaStatistics
} from '@/common/search-request';

export default {
    i18next: {
        ns: 'md-search-result'
    },
    props: ['theme', 'breadCrumbs'],
    computed: {
        ...rootMapState(usePageStore, {
            isSearchCenter: store => store.page.searchCenter,
            currencySymbol: store => store.currency.symbol,
            page: store => store.page,
            siteId: 'siteId'
        }),
        ...mapState({
            keywords: store => store.keywords
        }),

        isMobileSize() {
            return this.platform.isMobile;
        },
        level() {
            return this.breadCrumbs && this.breadCrumbs.length;
        },
        lastLevel() {
            return [...this.breadCrumbs].pop() || {};
        },
        prevDisabled({ slideIndex, startIndex }) {
            return !slideIndex || !startIndex;
        },
        nextDisabled({ mask, slideIndex, slides }) {
            return mask <= 0 || slideIndex === slides.length - 1;
        }
    },
    data: function() {
        return {
            mask: 0,
            taskLoading: [],
            slideIndex: 0,
            startIndex: 0,
            itemWidth: 0,
            slideColors: ['#1a88ff', '#25c44e', '#ffc836', '#ff827b'],
            slides: [],
            imgCdn: [
                'https://cdn.chime.me/image/fs/sitebuild/202137/18/original_e23fd086-b216-4726-919b-1c2ef89664a6.png', // blue
                'https://cdn.chime.me/image/fs/sitebuild/202137/18/original_7b7956e7-0414-4207-a15f-9e129615c8ab.png', // green
                'https://cdn.chime.me/image/fs/sitebuild/202137/18/original_0855652e-e104-4137-a8f8-f9479090e3f9.png', // yellow
                'https://cdn.chime.me/image/fs/sitebuild/202137/18/original_22470046-6279-426e-94ac-74df22e7c7a7.png' // red
            ]
        };
    },
    watch: {
        breadCrumbs: {
            handler() {
                this.slides = [];
                if (
                    Array.isArray(this.breadCrumbs) &&
                    this.breadCrumbs.length > 1
                ) { 
                    if (this.page.FEATURE_AREA_VERSION) {
                        this.featureAreaLoadNbrs();
                    } else {
                        this.loadNbrs();
                    }
                }
            },
            deep: true,
            immediate: true
        }
    },
    methods: {
        toNeighborhoodDetail(nbr) {
            if (this.page.FEATURE_AREA_VERSION) {
                if (nbr.key) {
                    window.open(nbr.key, '_blank');
                }
            } else {
                let item = Object.assign(nbr);
                let id = item.neighborhoodId;
                let url = `/api-site/hjNbrConfig/boundary?type=neighborhoods&name=${item.key.replace(
                    /\s/g,
                    '+'
                )}`;
                if (!('neighborhoodId' in item)) {
                    this.axiosIns
                        .get(url)
                        .then(res => {
                            if (res.neighborhoods && res.neighborhoods.length) {
                                item['neighborhoodId'] =
                                    res.neighborhoods[0].id;
                                this.toDetail(
                                    item['neighborhoodId'],
                                    item.name
                                );
                            } else {
                                console.warn('no neighborhood id');
                            }
                        })
                        .catch(e => {
                            console.error('request error', e);
                        });
                } else {
                    this.toDetail(id, item.name);
                }
            }
        },
        toDetail(id, name) {
            window.open(
                `${this.pageLocation.origin}/neighborhood/${id}/${name}`,
                '_blank'
            );
        },
        async func(item) {
            this.taskLoading.push(true);
            let info = await new Promise(resolve => {
                let params = {
                    siteId: this.siteId,
                    ...normalizeParams(
                        this.isSearchCenter,
                        {
                            key: item.key,
                            keywordType: 'hjNeighborhoods',
                            'listingStatus-multi-Back-on-Market': 'Active'
                        },
                        {},
                        { needKey: true }
                    ),
                    statTypes: 'PRICE_STATS',
                    cache: true,
                    timezone: this.getCookie('timezone') || util.getTimeZone()
                };
                return searchRealTimeListings
                    .call(this, {
                        params,
                        type: 'neighborStats',
                        globalData: this.page
                    })
                    .then(res => {
                        let data = res.statsResults ? res : res.data;
                        let { statsResults } = data;
                        const {
                            min = null,
                            max = null,
                            count = 0
                        } = statsResults.statResultList?.[0] || {};
                        const priceText = `${this.formatPrice(
                            min
                        )} - ${this.formatPrice(max)}`;
                        resolve({
                            min,
                            max,
                            priceText,
                            counts: count
                        });
                    })
                    .catch(e => {
                        console.error(e);
                        resolve(null);
                    });
            });
            Object.assign(item, info);
            this.taskLoading.pop();
            return info !== null;
        },
        async funcForNewFeatureArea(item) {
            try {
                let res = await getFeatureAreaStatistics({
                    siteId: this.siteId,
                    areaType: item.type,
                    area: `${item.name}, ${this.keywords?.[0]?.keyword || ''}`,
                    statsTypes: 'PRICE_STATS',
                    searchCenter: this.page.searchCenter
                });
                let data = res.statsResults ? res : res.data;
                let { statsResults } = data;
                const {
                    min = null,
                    max = null,
                    count = 0
                } = statsResults.statResultList?.[0] || {};
                const priceText = `${this.formatPrice(
                    min
                )} - ${this.formatPrice(max)}`;
                Object.assign(item, {
                    min,
                    max,
                    priceText,
                    counts: count
                });
            } catch (error) {
                console.error(error);
            } finally {
                this.taskLoading.pop();
            }
        },
        formatPrice(num) {
            if (num === null) {
                return this.$t('md-search-result.unknown'); 
            }
            if (num >= 1000 && num < 1000000) {
                return this.currencySymbol + parseInt(num / 1000) + 'K';
            } else if (num >= 1000000) {
                return this.currencySymbol + (num / 1000000).toFixed(1) + 'M';
            } else {
                return this.currencySymbol + num;
            }
        },
        loadNbrs() {
            let params = {
                siteId: this.siteId,
                ...normalizeParams(
                    this.isSearchCenter,
                    {
                        key: this.breadCrumbs[1].key,
                        keywordType: this.breadCrumbs[1].keywordType,
                        'listingStatus-multi-Back-on-Market': 'Active'
                    },
                    {},
                    { needKey: true }
                ),
                aggType: 'aggNeighborhood',
                aggSize: 10,
                cache: true,
                timezone: this.getCookie('timezone') || util.getTimeZone()
            };
            searchRealTimeListings
                .call(this, {
                    params,
                    type: 'nearbyNeighbor',
                    globalData: this.page
                })
                .then(res => {
                    let slides = res.data?.neighborhoodList || [];
                    let temp = [];
                    for (let i = 0; i < slides.length; i++) {
                        if (!temp.includes(slides[i].name)) {
                            slides[i].id = Math.random().toString(36).slice(2);
                            temp.push(slides[i].name);
                        } else {
                            slides.splice(i, 1);
                            i--;
                        }
                    }
                    this.slides = slides;
                    this.slides.length && this.$nextTick(this.resizeFn);
                });
        },
        prev() {
            if (this.prevDisabled) {
                return; 
            }
            this.slideIndex--;
            this.startIndex--;
        },
        next() {
            if (this.nextDisabled) {
                return; 
            }
            this.slideIndex++;
            this.startIndex++;
        },
        resizeFn() {
            try {
                const margin = 15;
                const containerWidth = this.$refs.transitionWrap.offsetWidth;
                this.itemWidth = this.$refs.item[0].offsetWidth + margin;
                this.mask =
                    this.slides.length -
                    Math.floor(containerWidth / this.itemWidth);
                const scrollIndex =
                    Math.floor(containerWidth / this.itemWidth) - 1;
                this.slideIndex =
                    scrollIndex > this.slides.length
                        ? this.slides.length
                        : scrollIndex;
                this.startIndex = 0;
                this.$refs.scrollWrap.scrollLeft = 0;
            } catch (error) {}
        },
        async featureAreaLoadNbrs() {
            try {
                let { data: neighborhoodList = {} } = await getNbrLinkForFeatureArea.call(this,
                    {
                        siteId: this.siteId,
                        searchCenter: this.page.searchCenter,
                        details: true,
                        areaType: 'city',
                        area: this.keywords?.[0]?.keyword || '',
                        aggType: 'aggNbrLinks'
                    });
                const nbrType = ['subdivision', 'community'];
                this.slides = [];
                let [city, state] = this.keywords[0].keyword.split(',');
                nbrType.forEach(type => {
                    for (let k in neighborhoodList[type] || {}) {
                        this.slides.push({
                            name: k,
                            docCount: neighborhoodList[type][k],
                            id: Math.random().toString(36).slice(2),
                            key: `/area/${city.toLowerCase()}${state}/${k.toLowerCase()}`.replaceAll(
                                ' ',
                                '-'
                            ),
                            type
                        });
                    }
                    this.slides.length && this.$nextTick(this.resizeFn);
                });
            } catch (error) {
                console.error(error);
            }
        }
    },
    mounted() {
        window.addEventListener('resize', this.resizeFn, false);
    },
    beforeUnmount() {
        window.removeEventListener('resize', this.resizeFn, false);
    }
};
</script>

<style lang="scss">
@import '../../theme/media.scss';
@import '../../theme/mixin.scss';
.explore-nbr.broker-listing-map,
.explore-nbr.broker-listing-market-insight,
.explore-nbr.broker-listing-list {
    .carousel-container {
        height: 160px;
        .scroll-wrap ul {
            margin-left: 5px;
            padding: 10px 0;
            overflow-y: unset;
            li {
                border-radius: 5px;
                box-shadow: 0 2px 10px 0 rgba(0, 10, 30, 0.1);
                background-color: #ffffff;
                border: none;
                position: relative;
                p {
                    border-bottom: 1px solid #dadada;
                    margin-bottom: 14px;
                    span {
                        font: 20px var(--font-bold);
                        color: #191919;
                    }
                }
                .item {
                    .iconfont {
                        color: #dadada;
                    }
                }
                &::after {
                    content: '';
                    width: 4px;
                    height: 20px;
                    background: #ff781d;
                    border-radius: 0 100px 100px 0;
                    position: absolute;
                    left: 0;
                    top: 22px;
                }
                &:nth-child(2n) {
                    &::after {
                        background: #23c864;
                    }
                }
                &:nth-child(3n) {
                    &::after {
                        background: #fb6f67;
                    }
                }
                &:nth-child(4n) {
                    &::after {
                        background: #45adff;
                    }
                }
            }
        }
        .iconarrow {
            text-align: center;
            &:not(.disabled):hover {
                color: #191919;
            }
            &.left,
            &.right {
                font-size: 26px;
                color: #191919;
            }
            &.prev {
                &.left {
                    transform: translate(-6px, -50%) rotate(-90deg);
                }
            }
            &.next {
                &.right {
                    transform: translate(6px, -50%) rotate(90deg);
                }
            }
            &.disabled {
                color: #dadada;
                cursor: not-allowed;
                opacity: 0.6;
            }
        }
    }
}
// template2
.explore-nbr.template1-listing-map,
.explore-nbr.template1-listing-market-insight,
.explore-nbr.template1-listing-list,
.explore-nbr.template2-listing-map,
.explore-nbr.template2-listing-market-insight,
.explore-nbr.template2-listing-list {
    background: #fff;
    .mg-title {
        color: #000;
    }
    .carousel-container {
        padding: 0 60px;

        .scroll-wrap {
            ul {
                li {
                    border: 1px solid #ebe8e8;
                    border-top-width: 4px;
                    border-top-style: solid;
                    border-top-color: 4px solid transparent;
                    background: #fff;
                    box-shadow: unset;
                    border-top-color: #1a88ff;
                    &:nth-child(2n) {
                        border-top-color: #25c44e;
                    }
                    &:nth-child(3n) {
                        border-top-color: #ffc836;
                    }
                    &:nth-child(4n) {
                        border-top-color: #ff827b;
                    }
                    &:hover {
                        border-left-style: solid;
                        border-right-style: solid;
                        border-bottom-style: solid;
                        border-left-width: 1px;
                        border-right-width: 1px;
                        border-bottom-width: 1px;
                        border-color: #1a88ff;
                        &:nth-child(2n) {
                            border-color: #25c44e;
                        }
                        &:nth-child(3n) {
                            border-color: #ffc836;
                        }
                        &:nth-child(4n) {
                            border-color: #ff827b;
                        }
                        .item {
                            color: inherit;
                        }
                        p,
                        span,
                        .iconfont {
                            color: inherit;
                        }
                    }
                    p {
                        line-height: 1;
                        span {
                            font-family: var(--font-bold);
                            font-size: 24px;
                            font-weight: 600;
                            color: #000;
                        }
                        .img-home {
                            display: inline-block;
                        }
                    }
                    span {
                        font-family: var(--font-normal);
                        font-size: 14px;
                        color: #787878;
                    }
                    .item {
                        .iconfont {
                            display: none;
                        }
                    }
                }
            }
        }
        .iconarrow {
            text-align: center;
            &:not(.disabled):hover {
                color: #1a88ff;
            }
            &.prev {
                transform: translate(11px, -50%);
            }
            &.next {
                transform: translate(-11px, -50%) rotate(180deg);
            }
            &.disabled {
                cursor: not-allowed;
                opacity: 0.6;
            }
        }
    }
}
// template3

.explore-nbr.template3-listing-map,
.explore-nbr.template3-listing-list,
.explore-nbr.template3-listing-market-insight {
    background: #fff;
    .mg-title {
        font-family: var(--font-bold);
        font-size: var(--mg-title-size);
        font-weight: bold;
    }
    .carousel-container {
        .scroll-wrap {
            ul {
                li {
                    border-top: 4px solid #03c9ff;
                    background: #fff;
                    &:hover {
                        background: #f8f9fd;
                    }
                    p {
                        border-bottom: 1px solid #f4f5f8;
                        span {
                            font-family: var(--font-bold);
                            font-size: 20px;
                            font-weight: bold;
                            color: #202437;
                        }
                    }
                    span {
                        font-family: var(--font-normal);
                        font-size: 14px;
                        color: #515666;
                    }
                    .iconfont {
                        color: #a0a3af;
                    }
                }
            }
        }
        .iconarrow {
            text-align: center;
            color: #515666;
            &.prev {
                transform: translate(-4px, -50%);
            }
            &.next {
                transform: translate(4px, -50%) rotate(180deg);
            }
            &.disabled {
                color: #a0a3af;
                cursor: not-allowed;
            }
            &:not(.disabled):hover {
                color: #000000;
            }
        }
    }
}

// template4,
.explore-nbr.template4-listing-map {
    background: #242a3d;
    .mg-title {
        font-family: var(--font-title);
        font-weight: 900;
        font-size: var(--mg-title-size);
        color: #fff;
        margin-bottom: 30px;
    }
    .carousel-container {
        .scroll-wrap {
            ul {
                li {
                    border-right: none;
                    border-top: none;
                    border-bottom: none;
                    border-left-width: 4px;
                    border-left-style: solid;
                    background: #33394c;
                    border-left-color: #1a88ff;
                    &:nth-child(2n) {
                        border-left-color: #25c44e;
                    }
                    &:nth-child(3n) {
                        border-left-color: #ffc836;
                    }
                    &:nth-child(4n) {
                        border-left-color: #ff827b;
                    }
                    &:hover {
                        background: #3f475e;
                    }
                    p {
                        border-bottom: none;
                        span {
                            font-family: var(--font-bold);
                            color: #fff;
                        }
                    }
                    span {
                        color: #8195bd;
                        font-family: var(--font-normal);
                    }
                    .iconfont {
                        color: #8195bd;
                    }
                    a {
                        padding: 20px 0 10px;
                    }
                }
            }
        }
    }
}
.explore-nbr.template4-listing-list,
.explore-nbr.template4-listing-market-insight {
    .mg-title {
        font-family: var(--font-title);
        font-size: var(--mg-title-size);
        font-weight: 900;
        color: #4a4a60;
        margin-bottom: 30px;
    }
    .carousel-container {
        .scroll-wrap {
            ul {
                li {
                    background-color: #f5f6fa;
                    border-left-width: 4px;
                    border-left-style: solid;
                    border-left-color: #1a88ff;
                    box-shadow: unset;
                    border-right: none;
                    border-top: none;
                    border-bottom: none;
                    &:nth-child(2n) {
                        border-left-color: #25c44e;
                    }
                    &:nth-child(3n) {
                        border-left-color: #ffc836;
                    }
                    &:nth-child(4n) {
                        border-left-color: #ff827b;
                    }
                    &:hover {
                        background: #eeeff6;
                    }
                    .item {
                        color: #8b93a7;
                        font-family: var(--font-normal);
                    }
                    p {
                        span {
                            font-family: var(--font-bold);
                        }
                    }
                }
            }
        }
        .iconarrow {
            text-align: center;
            line-height: 32px;
        }
    }
}

.explore-nbr.mg-container:not(.template4-listing-list, .template4-listing-map, .template4-listing-market-insight) {
    @media (max-width: 600px) {
        width: 100%;
        margin-left: 0;
        margin-right: 0;
    }
}
.explore-nbr.mg-container.template4-listing-list,
.explore-nbr.mg-container.template4-listing-map,
.explore-nbr.mg-container.template4-listing-market-insight {
    @media (max-width: 600px) {
        width: calc(100% - 30px);
        margin-left: 15px;
        margin-right: 15px;
    }
}
.explore-nbr {
    margin-top: 40px;
    margin-bottom: 40px;
    background: transparent;
    @media (max-width: 600px) {
        margin: 20px 0;
    }
    .loading-mask {
        background: rgba(255, 255, 255, 0);
    }
    .loading-wrap {
        transform: translate(-50%, -50%);
        background: #fff;
        box-shadow: unset;
        .loading.loading.loading {
            position: static;
        }
    }
    .mg-container {
        position: relative;
    }
    @include media-800 {
        .mg-title {
            font-size: var(--mg-title-size-mobile) !important;
        }
        .carousel-container {
            padding: 0 !important;
        }
    }
    .mg-title {
        color: #000;
        margin-bottom: 20px;
        padding-bottom: 0 !important;
        text-transform: uppercase;
        white-space: normal;
    }
    .carousel-container {
        position: relative;
        height: 150px;
        padding: 0 24px;
        @include media-800 {
            padding: 0;
        }
        .scroll-wrap {
            overflow-x: auto;
            height: 100%;
            ul {
                height: 100%;
                display: flex;
                flex-direction: row;
                align-items: center;
                li:last-child {
                    margin-left: 4px;
                }
                li {
                    margin-right: 15px;
                    min-width: 250px;
                    max-width: 250px;
                    height: 140px;
                    box-shadow: 0 1px 5px 0 rgba(0, 10, 30, 0.1);
                    padding: 20px 20px 0;
                    border: 1px solid #ebe8e8;
                    cursor: pointer;
                    &:last-child {
                        margin-right: 0;
                    }
                    p {
                        margin-bottom: 10px;
                        padding-bottom: 15px;
                        display: flex;
                        align-items: center;
                        span {
                            flex: 1;
                            font-family: Gotham;
                            font-size: 20px;
                            font-weight: bold;
                            color: #202437;
                            line-height: 1.2;
                            @include ellipsis();
                        }
                        .img-home {
                            width: 32px;
                            height: 32px;
                            display: none;
                            margin-right: 8px;
                        }
                    }
                    .item {
                        margin-bottom: 10px;
                        color: #515666;
                        font-family: Gotham;
                        font-size: 14px;
                        line-height: 1;
                        .iconfont {
                            font-size: 14px;
                            margin-right: 6px;
                        }
                    }
                }
            }
        }
        @include media-800 {
            .iconarrow,
            .iconimg {
                display: none;
            }
        }
        .iconarrow,
        .iconimg {
            position: absolute;
            font-size: 32px;
            color: #adb9d1;
            width: 32px;
            height: 32px;
            border-radius: 50%;
            text-align: center;
            line-height: 32px;
            cursor: pointer;
            &.prev {
                left: 0;
                top: 50%;
                transform: translate(10px, -50%);
            }
            &.next {
                right: 0;
                top: 50%;
                transform: translate(-10px, -50%) rotate(180deg);
            }
            &.icon-v3 {
                font-size: 16px;
                &.disabled {
                    color: #a0a3af;
                }
                &.hover {
                    color: #000000;
                }
            }
        }
        .iconimg {
            &.light {
                background: url(https://cdn.chime.me/image/fs/sitebuild/2021318/2/original_8e24a201-62bd-4252-9bc1-fa9d52ca6a5f.png)
                    no-repeat center/contain;
                &.disabled {
                    background: url(https://cdn.chime.me/image/fs/sitebuild/2021318/2/original_968998c2-c552-4578-9117-5b979c2588c6.png)
                        no-repeat center/contain;
                    cursor: not-allowed;
                }
                &:not(.disabled):hover {
                    background: url(https://cdn.chime.me/image/fs/sitebuild/2021318/2/original_f239c024-aa41-4890-b0b8-904878678fc1.png)
                        no-repeat center/contain;
                    &.next {
                        transform: translate(-10px, -50%);
                    }
                    &.prev {
                        transform: translate(10px, -50%) rotate(180deg);
                    }
                }
            }
            &.deep {
                background: url(https://cdn.chime.me/image/fs/sitebuild/2021318/1/original_306e557c-e86e-43f1-ad08-2f2dc8fd58b5.png)
                    no-repeat center/contain;
                &.disabled {
                    background: url(https://cdn.chime.me/image/fs/sitebuild/2021318/1/original_f6d23d1e-71f1-4307-bb1e-70abfd2ce7c0.png)
                        no-repeat center/contain;
                    cursor: not-allowed;
                }
                &:not(.disabled):hover {
                    background: url(https://cdn.chime.me/image/fs/sitebuild/2021318/1/original_6491a0e1-3a36-46b3-8f67-fa3e3474de12.png)
                        no-repeat center/contain;
                    &.next {
                        transform: translate(-10px, -50%);
                    }
                    &.prev {
                        transform: translate(10px, -50%) rotate(180deg);
                    }
                }
            }
        }
    }
}
</style>
